import React, { useState, useEffect } from 'react';
import { Loader2, Factory, Eye, ShoppingBag, CreditCard, RefreshCw, ExternalLink, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Procure1688Panel({ order, token, onUpdated }) {
  const [addresses, setAddresses] = useState(null);
  const [addressId, setAddressId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState(order.procurement?.last_check || null);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const proc = order.procurement;
  const is1688 = /1688\.com\/offer\/\d+/.test(order.product_url || '') || (order.sku_selections || []).some((s) => s.name === 'Item ID' && /^1688 #/.test(s.value));

  useEffect(() => {
    if (!is1688 || proc) return;
    fetch(`${API}/api/procurement/addresses`, { headers }).then((r) => r.json()).then((d) => {
      if (d.success) { setAddresses(d.data); const def = d.data.find((a) => a.is_default) || d.data[0]; if (def) setAddressId(String(def.id)); }
      else toast.error(d.detail || 'Could not load 1688 addresses');
    }).catch(() => toast.error('Could not load 1688 addresses'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const call = async (label, path, opts = {}) => {
    setBusy(label);
    try {
      const res = await fetch(`${API}/api/procurement/${order.id}/${path}`, { headers, ...opts });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `${label} failed`);
      return data;
    } catch (err) { toast.error(err.message); return null; }
    finally { setBusy(''); }
  };

  if (!is1688) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm space-y-3" data-testid={`procure-panel-${order.id}`}>
      <div className="flex items-center gap-2 font-semibold text-amber-900"><Factory className="w-4 h-4" /> Buy on 1688 (via API)</div>

      {!proc ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
            <label className="block">
              <span className="text-xs text-amber-800">Warehouse delivery address (1688 account)</span>
              <select value={addressId} onChange={(e) => { setAddressId(e.target.value); setPreview(null); }} className="mt-1 w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white" data-testid={`procure-address-${order.id}`}>
                {!addresses && <option>Loading addresses…</option>}
                {(addresses || []).map((a) => <option key={a.id} value={a.id}>{a.full_name} · {a.area} {a.address} · {a.mobile}</option>)}
              </select>
            </label>
            <Button onClick={async () => { const d = await call('Preview', 'preview', { method: 'POST', body: JSON.stringify({ address_id: Number(addressId) }) }); if (d) setPreview(d.data); }}
              disabled={!addressId || !!busy} variant="outline" className="border-amber-400 text-amber-900 hover:bg-amber-100 rounded-lg" data-testid={`procure-preview-btn-${order.id}`}>
              {busy === 'Preview' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />} Preview 1688 price
            </Button>
          </div>

          {preview && (
            <div className="bg-white rounded-lg border border-amber-200 p-3 space-y-1" data-testid={`procure-preview-${order.id}`}>
              <div className="flex justify-between"><span className="text-gray-500">Offer / Spec</span><span className="font-mono text-xs">{preview.offer_id}{preview.spec_id ? ` · ${preview.spec_id.slice(0, 8)}…` : ''}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Quantity</span><span>{preview.quantity} × ¥{preview.unit_price_rmb}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Goods</span><span>¥{preview.goods_rmb.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Domestic shipping</span><span>¥{preview.shipping_rmb.toFixed(2)}</span></div>
              {preview.discount_rmb > 0 && <div className="flex justify-between text-emerald-700"><span>Promotion</span><span>−¥{preview.discount_rmb.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold border-t border-gray-100 pt-1"><span>1688 total to pay</span><span className="text-orange-600" data-testid={`procure-total-${order.id}`}>¥{preview.total_rmb.toFixed(2)}</span></div>
              {order.total_rmb && preview.total_rmb > order.total_rmb && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> 1688 total is above the customer's quoted ¥{order.total_rmb.toFixed(2)} — confirm before ordering.</p>}
              <Button onClick={async () => { const d = await call('Create', 'create', { method: 'POST', body: JSON.stringify({ address_id: Number(addressId), message: `RTR order ${order.id}` }) }); if (d) { toast.success(`1688 order ${d.data.alibaba_order_id} created`); onUpdated(); } }}
                disabled={!!busy || !preview.ok} className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg" data-testid={`procure-create-btn-${order.id}`}>
                {busy === 'Create' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingBag className="w-4 h-4 mr-2" />} Place order on 1688 (¥{preview.total_rmb.toFixed(2)})
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg border border-amber-200 p-3 space-y-2" data-testid={`procure-created-${order.id}`}>
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="w-4 h-4" /> 1688 order <span className="font-mono">{proc.alibaba_order_id}</span> · ¥{proc.total_rmb?.toFixed(2)}</div>
          <p className="text-xs text-gray-500">Created {new Date(proc.created_at).toLocaleString()} by {proc.created_by}. Status: <strong data-testid={`procure-status-${order.id}`}>{status?.status_text || proc.status}</strong>{status?.pay_time ? ` · paid ${status.pay_time}` : ''}</p>
          {status?.tracking?.length > 0 && <p className="text-xs text-gray-700">Tracking: {status.tracking.map((t) => `${t.company} ${t.no}`).join(', ')}</p>}
          <div className="flex flex-wrap gap-2">
            {[['alipay', 'Alipay / WeChat'], ['crossborder', 'Cross-border pay']].map(([ch, label]) => (
              <Button key={ch} size="sm" variant="outline" disabled={!!busy} onClick={async () => { const d = await call(label, `pay-url?channel=${ch}`); if (d) window.open(d.pay_url, '_blank'); }}
                className="border-orange-300 text-orange-700 hover:bg-orange-50 rounded-lg" data-testid={`procure-pay-${ch}-${order.id}`}>
                {busy === label ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />} Pay via {label} <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            ))}
            <Button size="sm" variant="outline" disabled={!!busy} onClick={async () => { const d = await call('Status', 'status'); if (d) { setStatus(d.data); onUpdated(); } }}
              className="rounded-lg" data-testid={`procure-status-btn-${order.id}`}>
              {busy === 'Status' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Refresh 1688 status
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
